import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, DollarSign } from 'lucide-react';
import { MiniGameResult, PersonalityType } from '@/types/career-sim';
import MiniGameWrapper from './MiniGameWrapper';

interface MarketingGameProps {
  onComplete: (result: MiniGameResult) => void;
  onExit: () => void;
  personality: PersonalityType;
}

interface Platform {
  id: string;
  name: string;
  icon: string;
  allocation: number;
  multiplier: number;
}

const MarketingGame: React.FC<MarketingGameProps> = ({ onComplete, onExit, personality }) => {
  const totalBudget = 500;
  const maxTime = 20;
  const [timeLeft, setTimeLeft] = useState(maxTime);
  const [platforms, setPlatforms] = useState<Platform[]>([
    { id: 'instagram', name: 'Instagram', icon: '📸', allocation: 0, multiplier: 1 },
    { id: 'tiktok', name: 'TikTok', icon: '🎵', allocation: 0, multiplier: 1 },
    { id: 'spotify', name: 'Spotify Ads', icon: '🎧', allocation: 0, multiplier: 1 },
    { id: 'youtube', name: 'YouTube', icon: '📺', allocation: 0, multiplier: 1 },
  ]);

  // Apply personality multipliers
  useEffect(() => {
    setPlatforms(prev => prev.map(p => {
      let multiplier = 1;
      if (personality === 'trendsetter' && p.id === 'tiktok') multiplier = 2;
      if (personality === 'hustler' && p.id === 'instagram') multiplier = 1.5;
      if (personality === 'purist' && p.id === 'spotify') multiplier = 1.3;
      return { ...p, multiplier };
    }));
  }, [personality]);

  useEffect(() => {
    if (timeLeft <= 0) {
      endGame();
      return;
    }

    const timer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [timeLeft]);

  const handleAllocationChange = (platformId: string, value: number) => {
    setPlatforms(prev => {
      const updated = prev.map(p => 
        p.id === platformId ? { ...p, allocation: value } : p
      );
      
      // Calculate total and adjust if over budget
      const total = updated.reduce((sum, p) => sum + p.allocation, 0);
      if (total > totalBudget) {
        const excess = total - totalBudget;
        const platformToAdjust = updated.find(p => p.id === platformId);
        if (platformToAdjust) {
          platformToAdjust.allocation = Math.max(0, platformToAdjust.allocation - excess);
        }
      }
      
      return updated;
    });
  };

  const endGame = () => {
    // Calculate fans gained based on allocation and multipliers
    const totalFans = platforms.reduce((sum, p) => {
      return sum + (p.allocation * p.multiplier * 0.5); // 0.5 fans per dollar
    }, 0);

    const totalSpent = platforms.reduce((sum, p) => sum + p.allocation, 0);
    const efficiency = totalSpent > 0 ? (totalFans / totalSpent) : 0;

    let multiplier = 1;
    if (efficiency > 0.6) multiplier = 1.5;
    else if (efficiency > 0.4) multiplier = 1.2;
    else if (efficiency < 0.2) multiplier = 0.8;

    onComplete({
      score: Math.round(totalFans),
      multiplier,
      success: totalSpent > 0
    });
  };

  const allocatedBudget = platforms.reduce((sum, p) => sum + p.allocation, 0);
  const remainingBudget = totalBudget - allocatedBudget;

  return (
    <MiniGameWrapper
      title="Marketing Campaign"
      instructions="Allocate your $500 budget across platforms for maximum reach!"
      timeLeft={timeLeft}
      maxTime={maxTime}
      onExit={onExit}
    >
      <div className="space-y-4">
        {/* Budget Display */}
        <div className="flex items-center justify-between p-4 bg-gradient-to-r from-green-500/20 to-blue-500/20 rounded-lg border border-white/10">
          <div className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-green-400" />
            <span className="text-sm text-muted-foreground">Remaining:</span>
          </div>
          <span className="text-2xl font-bold text-green-400">${remainingBudget}</span>
        </div>

        {/* Platform Sliders */}
        <div className="space-y-4">
          {platforms.map(platform => (
            <motion.div
              key={platform.id}
              className="p-4 bg-card/50 rounded-lg border border-border"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{platform.icon}</span>
                  <span className="font-semibold">{platform.name}</span>
                  {platform.multiplier > 1 && (
                    <span className="text-xs px-2 py-1 bg-purple-500/20 text-purple-400 rounded-full">
                      {platform.multiplier}x
                    </span>
                  )}
                </div>
                <span className="text-lg font-bold text-primary">${platform.allocation}</span>
              </div>
              
              <input
                type="range"
                min="0"
                max={totalBudget}
                step="25"
                value={platform.allocation}
                onChange={(e) => handleAllocationChange(platform.id, parseInt(e.target.value))}
                className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer slider"
              />
              
              <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                <TrendingUp className="w-3 h-3" />
                <span>~{Math.round(platform.allocation * platform.multiplier * 0.5)} fans</span>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Finish Button */}
        <motion.button
          className="w-full py-3 bg-primary text-primary-foreground rounded-lg font-semibold"
          onClick={endGame}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          disabled={allocatedBudget === 0}
        >
          Launch Campaign
        </motion.button>
      </div>
    </MiniGameWrapper>
  );
};

export default MarketingGame;
